import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  useToast,
} from '@chakra-ui/react'
import { FiTrash2, FiAlertTriangle } from 'react-icons/fi'
import { Folder } from '@/types'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface DeleteFolderModalProps {
  isOpen: boolean
  onClose: () => void
  folder: Folder
  onDelete: (folderId: number) => void
}

export default function DeleteFolderModal({
  isOpen,
  onClose,
  folder,
  onDelete,
}: DeleteFolderModalProps) {
  const [confirmationText, setConfirmationText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()
  const { t } = useAppTranslation(['files', 'common'])

  useEffect(() => {
    if (isOpen) {
      setConfirmationText('')
    }
  }, [isOpen])

  const isConfirmationValid = confirmationText === folder?.name

  const handleDelete = async () => {
    if (!isConfirmationValid) {
      return
    }

    setIsLoading(true)
    try {
      onDelete(folder.id)
      onClose()

      toast({
        title: t('folders.deleteFolderSuccess', { ns: 'files' }),
        description: t('folders.deleteFolderSuccessDescription', {
          ns: 'files',
          folderName: folder.name,
        }),
        status: 'success',
        duration: 4000,
        isClosable: true,
      })
    } catch (error: any) {
      console.error('Failed to delete folder:', error)
      toast({
        title: t('status.error', { ns: 'common' }),
        description:
          error.response?.data?.message || t('folders.deleteFolderError', { ns: 'files' }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setConfirmationText('')
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" overflow="hidden">
        <ModalHeader
          background="linear-gradient(135deg, var(--chakra-colors-red-500), var(--chakra-colors-red-700))"
          color="white"
          borderTopRadius="xl"
          borderBottom="1px solid"
          borderColor="red.600"
        >
          <span>{t('folders.deleteFolder', { ns: 'files' })}</span>
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'whiteAlpha.200' }} />

        <ModalBody pt={6} pb={2}>
          <VStack spacing={5} align="stretch">
            {/* Warning Alert */}
            <Alert
              status="error"
              borderRadius="lg"
              bg="red.50"
              border="1px solid"
              borderColor="red.200"
            >
              <AlertIcon as={FiAlertTriangle} color="red.500" alignSelf="flex-start" />
              <Box>
                <AlertTitle color="red.800" fontSize="sm" fontWeight="bold">
                  {t('folders.deleteWarningTitle', { ns: 'files' })}
                </AlertTitle>
                <AlertDescription color="red.700" fontSize="sm" mt={1}>
                  {t('folders.deleteWarningDescription', {
                    ns: 'files',
                    folderName: folder?.name,
                  })}
                  <VStack align="start" mt={2} spacing={1} fontSize="xs">
                    <Text>• {t('folders.deleteWarningFiles', { ns: 'files' })}</Text>
                    <Text>• {t('folders.deleteWarningSubfolders', { ns: 'files' })}</Text>
                    <Text>• {t('folders.deleteWarningMetadata', { ns: 'files' })}</Text>
                  </VStack>
                </AlertDescription>
              </Box>
            </Alert>

            {/* Confirmation Input */}
            <FormControl>
              <FormLabel color="gray.700" fontWeight="semibold" mb={2}>
                <VStack align="start" spacing={1}>
                  <Text>{t('folders.deleteConfirmationLabel', { ns: 'files' })}</Text>
                  <Text fontSize="sm" color="gray.600" fontWeight="normal">
                    {t('folders.deleteConfirmationHelper', {
                      ns: 'files',
                      folderName: folder?.name,
                    })}
                  </Text>
                </VStack>
              </FormLabel>
              <Input
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={t('folders.deleteConfirmationPlaceholder', {
                  ns: 'files',
                  folderName: folder?.name,
                })}
                size="md"
                borderRadius="lg"
                bg="gray.50"
                border="1px solid"
                borderColor={confirmationText && !isConfirmationValid ? 'red.300' : 'gray.200'}
                _hover={{
                  borderColor: confirmationText && !isConfirmationValid ? 'red.400' : 'gray.300',
                  bg: 'white',
                }}
                _focus={{
                  borderColor: confirmationText && !isConfirmationValid ? 'red.500' : 'blue.500',
                  bg: 'white',
                  boxShadow:
                    confirmationText && !isConfirmationValid
                      ? '0 0 0 1px var(--chakra-colors-red-500)'
                      : '0 0 0 1px var(--chakra-colors-blue-500)',
                }}
              />
              {confirmationText && !isConfirmationValid && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {t('folders.deleteConfirmationMismatch', {
                    ns: 'files',
                    folderName: folder?.name,
                  })}
                </Text>
              )}
              {isConfirmationValid && (
                <Text color="green.500" fontSize="sm" mt={1}>
                  {t('folders.deleteConfirmationValid', { ns: 'files' })}
                </Text>
              )}
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter
          borderTop="1px solid"
          borderColor="gray.200"
          bg="gray.50"
          borderBottomRadius="xl"
        >
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isLoading}
            _hover={{ bg: 'gray.100' }}
          >
            {t('buttons.cancel', { ns: 'common' })}
          </Button>
          <Button
            colorScheme="red"
            onClick={handleDelete}
            isLoading={isLoading}
            loadingText={t('folders.deleting', { ns: 'files' })}
            isDisabled={!isConfirmationValid}
          >
            {t('folders.deleteForever', { ns: 'files' })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
